import React from 'react';
import { motion } from 'framer-motion';
import {
  RotateCcw,
  Calculator,
  Brain,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MinusCircle,
  Repeat,
} from 'lucide-react';
import { TNaerasJob, TNaerasJobRun } from '@/data/types';
import { JOB_META, JOB_ORDER, formatAthensTime, jobOutcome, parseJobDate } from '../utils/jobs';
import { ManualTag } from './JobRunRow';

interface TodayJobCardsProps {
  /** Today's run per job, or undefined where the job hasn't run yet. */
  runsToday: Partial<Record<TNaerasJob, TNaerasJobRun>>;
  isLoading: boolean;
  /** The backend's today, so a pending retry can be told from a final failure. */
  todayStr: string;
}

const JOB_ICONS: Record<TNaerasJob, React.ComponentType<{ className?: string }>> = {
  A: RotateCcw,
  B: Calculator,
  C: Brain,
  D: Download,
};

interface ToneStyle {
  icon: React.ComponentType<{ className?: string }>;
  chip: string;
  iconBox: string;
  ring: string;
}

const TONE_STYLES: Record<'success' | 'warning' | 'danger' | 'neutral', ToneStyle> = {
  success: {
    icon: CheckCircle2,
    chip: 'bg-emerald-100 text-emerald-800',
    iconBox: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    ring: 'border-emerald-200',
  },
  warning: {
    icon: Loader2,
    chip: 'bg-amber-100 text-amber-900',
    iconBox: 'bg-gradient-to-br from-amber-500 to-orange-600',
    ring: 'border-amber-200',
  },
  danger: {
    icon: AlertCircle,
    chip: 'bg-rose-100 text-rose-800',
    iconBox: 'bg-gradient-to-br from-rose-500 to-red-600',
    ring: 'border-rose-200',
  },
  neutral: {
    icon: MinusCircle,
    chip: 'bg-slate-200 text-slate-800',
    iconBox: 'bg-gradient-to-br from-slate-500 to-slate-700',
    ring: 'border-slate-200',
  },
};

/**
 * Did today's jobs run? One card per job, answering exactly that.
 *
 * Four cards share the page's 1280px container, so each gets roughly 290px.
 * The layout is built for that width: the status chip sits on its own line
 * beside the job letter rather than fighting the title for space, titles and
 * descriptions are clamped so every card keeps the same silhouette, and the
 * footer always renders its three rows so the values line up across the row
 * even when a job has no run to report.
 */
export const TodayJobCards: React.FC<TodayJobCardsProps> = ({ runsToday, isLoading, todayStr }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {JOB_ORDER.map((job, index) => {
        const meta = JOB_META[job];
        const run = runsToday[job];
        const outcome = run ? jobOutcome(run, todayStr) : null;
        const styles = outcome ? TONE_STYLES[outcome.tone] : TONE_STYLES.neutral;
        const StatusIcon = styles.icon;
        const JobIcon = JOB_ICONS[job];
        const startedAt = parseJobDate(run?.started_at);
        const hasRetries = Boolean(run && run.attempts > 1);

        return (
          <motion.div
            key={job}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.08 * (index + 1) }}
            className="h-full"
          >
            <div
              className={`flex h-full flex-col rounded-3xl border bg-white p-4 shadow-xl shadow-gray-900/5 transition-all duration-300 hover:shadow-2xl hover:shadow-gray-900/10 sm:p-5 ${
                run ? styles.ring : 'border-gray-100'
              }`}
            >
              {/* Job letter and status, each with room of its own */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
                      run ? styles.iconBox : 'bg-gray-200'
                    } text-white shadow-sm`}
                  >
                    <JobIcon className="h-4 w-4" />
                  </div>
                  <span className="truncate text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Εργασία {job}
                  </span>
                  {run?.manual && <ManualTag />}
                </div>

                {isLoading && !run ? (
                  <span className="h-5 w-16 flex-shrink-0 animate-pulse rounded-full bg-gray-100" />
                ) : (
                  <span
                    title={outcome?.label}
                    className={`flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      run ? styles.chip : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <StatusIcon
                      className={`h-3 w-3 flex-shrink-0 ${
                        run && outcome?.tone === 'warning' ? 'animate-spin' : ''
                      }`}
                    />
                    {outcome ? outcome.short : 'Δεν έτρεξε'}
                  </span>
                )}
              </div>

              {/* Two lines of title keep every card the same height */}
              <h3 className="mt-3 line-clamp-2 min-h-[2.25rem] text-sm font-bold leading-snug text-gray-900">
                {meta.title}
              </h3>

              <p className="mt-1 line-clamp-3 text-xs leading-snug text-gray-600">
                {meta.description}
              </p>

              {/* Pinned to the bottom, always three rows */}
              <dl className="mt-auto space-y-1.5 border-t border-gray-100 pt-3 text-[11px]">
                <div className="flex items-start justify-between gap-2">
                  <dt className="flex-shrink-0 font-semibold text-gray-500">Πρόγραμμα</dt>
                  <dd className="text-right leading-snug text-gray-700">{meta.schedule}</dd>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <dt className="flex-shrink-0 font-semibold text-gray-500">Έναρξη</dt>
                  <dd className="text-right font-mono text-gray-700">
                    {startedAt ? formatAthensTime(startedAt) : '—'}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <dt
                    className={`flex flex-shrink-0 items-center gap-1 font-semibold ${
                      hasRetries ? 'text-amber-700' : 'text-gray-500'
                    }`}
                  >
                    {hasRetries && <Repeat className="h-3 w-3" />}
                    Προσπάθειες
                  </dt>
                  <dd
                    className={`text-right font-mono ${
                      hasRetries ? 'font-bold text-amber-800' : 'text-gray-700'
                    }`}
                  >
                    {run ? run.attempts : '—'}
                  </dd>
                </div>
              </dl>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default TodayJobCards;
